import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "345",
  "slug": "team-building-games-small-teams",
  "title": "The Best Team-Building Games for Small Teams (Under 10 People)",
  "excerpt": "Most team-building advice is written for big groups. With a small team you need games that include everyone and don't feel like a forced corporate exercise.",
  "date": "2026-06-04",
  "category": "Team Building",
  "image": "https://images.unsplash.com/photo-1517048676732-d65bc937f952?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxzbWFsbCUyMHRlYW0lMjBtZWV0aW5nfGVufDF8MHx8fDE3ODA1NjA0NzZ8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "content": [
    {
      "type": "paragraph",
      "text": "Team-building games tend to be designed for the conference hall — big numbers, breakout groups, lots of moving parts. A team of six is a completely different animal. Everyone is visible, nobody can hide at the back, and the awkwardness of a forced icebreaker is amplified. Small teams need a different toolkit."
    },
    {
      "type": "heading",
      "text": "What Small Teams Need"
    },
    {
      "type": "paragraph",
      "text": "The best small-group games share a few traits: almost no setup, full participation by default, a touch of friendly competition, and a short runtime so they energize rather than drain. They should also work whether your team is around one table or scattered across a video call. If a game needs a facilitator, a slide deck, and twenty minutes of rules, it is built for a different size of room."
    },
    {
      "type": "heading",
      "text": "Games That Work for 4 to 10 People"
    },
    {
      "type": "list",
      "items": [
        "Two Truths and a Map — each person names two real places they have been and one they have not; everyone else guesses the lie. Travel stories beat generic facts because they spark follow-up questions.",
        "Geography guessing rounds — share one screen and have the whole team call out guesses on a satellite or street view, either racing each other or pooling clues to solve it together.",
        "Where Would You Live — each person drops a pin anywhere on the world map and gets 30 seconds to defend the choice. Invite people to explain their choice, with an option to pass.",
        "20 Questions: Countries — one person picks a country, the team narrows it down with yes-or-no questions. Quick, collaborative, and quietly competitive.",
        "Pitch the Offsite — everyone pitches a dream destination in 60 seconds, then the team votes on the winner. Doubles as actual planning input.",
        "Geography Pictionary or charades, themed around landmarks and capitals, for teams that like to get out of their chairs."
      ]
    },
    {
      "type": "heading",
      "text": "For Remote Small Teams"
    },
    {
      "type": "paragraph",
      "text": "Distributed teams do not need a separate 'fun event' on the calendar — that often backfires. Instead, screen-share a quick map game at the start of a regular call. A single guessing round before standup costs five minutes, gets people talking, and is far more effective than a scheduled hour of mandatory bonding that everyone quietly dreads. The casualness is the feature, not a compromise."
    },
    {
      "type": "heading",
      "text": "The Mistakes to Avoid"
    },
    {
      "type": "paragraph",
      "text": "Two traps sink small-team games. The first is the over-talker: with only a handful of people, one dominant voice can take over, so use formats with turns built in. The second is over-engineering — the more rules and props you add, the more it feels like work. Read the room, too. A team coming off a brutal deadline wants something light and short, not a competitive gauntlet."
    },
    {
      "type": "heading",
      "text": "Why It's Worth the Five Minutes"
    },
    {
      "type": "paragraph",
      "text": "A small team lives or dies on trust, and trust is built in small, repeated moments rather than one annual away-day. A quick game gives quieter members a low-stakes way to be heard, surfaces shared interests that never come up in standup, and resets the mood after a tense sprint. Teams that laugh together tend to disagree more openly and recover from mistakes faster — the payoff shows up in the actual work, not just the calendar invite. None of that requires a budget or an offsite; it requires a habit."
    },
    {
      "type": "heading",
      "text": "Make It Stick"
    },
    {
      "type": "paragraph",
      "text": "If a game lands, fold it into a regular rhythm — the first five minutes of the Monday call, or a Friday wind-down round. A predictable, lightweight ritual beats the occasional grand event, because people come to look forward to it instead of bracing for it. Keep a short list of three or four games that have worked, retire any that go stale, and let different people take the lead so it never feels like one person's pet project. The teams that keep these rituals alive are usually the ones that treat them as five minutes well spent rather than a chore to schedule."
    },
    {
      "type": "heading",
      "text": "Keep It Light"
    },
    {
      "type": "paragraph",
      "text": "With a small team the goal is shared laughter and a little competition, not a list of learning outcomes. Rotate who picks the game, keep rounds short, and stop while people still want more. A quick EarthGuessr round is about as low-effort as team warm-ups get — one shared screen, everyone guessing, no preparation required."
    }
  ]
};
